import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { QrCode, Copy, Download } from "lucide-react";
import { copyAppLink } from "../lib/share";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

const DEFAULT_APP_URL = "https://tour.trigoride.com";

/**
 * QR code modal — generates a QR pointing to a target URL (defaults to the app
 * landing page) using the public api.qrserver.com service. Client can screenshot /
 * save / print for physical sharing.
 *
 * Props:
 *   - open, onOpenChange       — dialog state
 *   - url        (optional)    — override the URL encoded in the QR
 *   - title      (optional)    — dialog heading
 *   - description(optional)    — small text below the heading
 *   - filename   (optional)    — download filename (without extension)
 */
export default function ShareQrDialog({ open, onOpenChange, url, title, description, filename }) {
  const targetUrl = url || DEFAULT_APP_URL;
  const finalTitle = title || "Partager TrigoTour";
  const finalDesc  = description || "Scannez ce QR code pour ouvrir tour.trigoride.com — parfait pour flyers, cartes de visite ou vitrines.";
  const finalFile  = (filename || "trigo-tour-qr") + ".png";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=520x520&margin=16&format=png&data=${encodeURIComponent(targetUrl)}`;
  const download = () => {
    const a = document.createElement("a");
    a.href = qrUrl;
    a.download = finalFile;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const copyThisLink = async () => {
    try {
      await navigator.clipboard.writeText(targetUrl);
      const { toast } = await import("sonner");
      toast.success("Lien copié dans le presse-papier");
    } catch { /* noop */ }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="share-qr-dialog">
        <VisuallyHidden.Root>
          <DialogTitle>{finalTitle}</DialogTitle>
          <DialogDescription>{finalDesc}</DialogDescription>
        </VisuallyHidden.Root>

        <div className="flex flex-col items-center gap-3 pt-2">
          <div className="w-14 h-14 rounded-2xl bg-[#FACC15]/20 flex items-center justify-center">
            <QrCode className="w-7 h-7 text-[#0A2540]" />
          </div>
          <h2 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
            {finalTitle}
          </h2>
          <p className="text-xs text-[#64748B] text-center" dangerouslySetInnerHTML={{ __html: finalDesc }} />

          <div className="p-3 bg-white rounded-2xl border-2 border-[#0A2540]/10">
            <img
              src={qrUrl}
              alt="QR code TrigoTour"
              className="w-64 h-64 block"
              data-testid="share-qr-image"
            />
          </div>

          <p className="text-[11px] font-mono text-[#64748B] text-center break-all">{targetUrl}</p>

          <div className="grid grid-cols-2 gap-2 w-full mt-2">
            <button
              type="button"
              onClick={url ? copyThisLink : copyAppLink}
              className="h-11 rounded-full bg-[#F1F5F9] text-[#0A2540] font-semibold text-sm inline-flex items-center justify-center gap-1.5 hover:bg-[#E2E8F0]"
              data-testid="share-qr-copy-btn"
            >
              <Copy className="w-4 h-4" /> Copier le lien
            </button>
            <button
              type="button"
              onClick={download}
              className="h-11 rounded-full bg-[#FACC15] text-[#0A2540] font-bold text-sm inline-flex items-center justify-center gap-1.5 hover:bg-[#EAB308]"
              data-testid="share-qr-download-btn"
            >
              <Download className="w-4 h-4" /> Télécharger
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
